import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../services/apiClient';

// Mobile "Daily Podcast" — pick a channel (Executive, a PM, Shop, a Work Flow),
// then play that channel's brief. Audio is only fetched once you hit play.
const NAVY = '#1e3a5f';
const ORANGE = '#e87722';

const fmtTime = s => {
  if (!s || isNaN(s)) return '0:00';
  const m = Math.floor(s / 60), sec = Math.floor(s % 60);
  return `${m}:${sec < 10 ? '0' : ''}${sec}`;
};
const fmtDay = d => {
  if (!d) return '';
  const dt = new Date(String(d).length <= 10 ? d + 'T12:00:00' : d);
  return isNaN(dt) ? d : dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};
const groupIcon = a => (a === 'exec' ? '🏢' : a === 'pm' ? '👤' : a === 'shop' ? '🔧' : '🛠️');
const GROUP_ORDER = { exec: 0, pm: 1, shop: 2, workflow: 3 };
const GROUP_TITLE = { exec: 'Company', pm: 'Project Managers', shop: 'Shop', workflow: 'Work Flows' };

function Player({ brief }) {
  const ref = React.useRef(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(brief.duration_seconds || 0);
  useEffect(() => { setPlaying(false); setCur(0); setDur(brief.duration_seconds || 0); }, [brief.id, brief.duration_seconds]);

  if (!brief.audio_url) {
    return <div style={{ background: '#f8fafc', border: '1px solid #eef2f7', borderRadius: 10, padding: '12px 14px', color: '#9ca3af', fontSize: 13 }}>🎙️ No audio posted for this day yet.</div>;
  }
  const toggle = () => { const a = ref.current; if (!a) return; if (a.paused) { a.play(); setPlaying(true); } else { a.pause(); setPlaying(false); } };
  const seek = e => {
    const a = ref.current; if (!a || !dur) return;
    const r = e.currentTarget.getBoundingClientRect();
    a.currentTime = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)) * dur;
    setCur(a.currentTime);
  };
  const pct = dur ? Math.min(100, (cur / dur) * 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#f8fafc', border: '1px solid #eef2f7', borderRadius: 10, padding: '12px 14px' }}>
      <audio ref={ref} src={brief.audio_url} preload="none"
        onLoadedMetadata={e => setDur(e.target.duration || brief.duration_seconds || 0)}
        onTimeUpdate={e => setCur(e.target.currentTime)} onEnded={() => setPlaying(false)} />
      <button onClick={toggle} aria-label={playing ? 'Pause' : 'Play'}
        style={{ flexShrink: 0, width: 52, height: 52, borderRadius: '50%', border: 'none', background: NAVY, color: '#fff', fontSize: 21, cursor: 'pointer', lineHeight: 1 }}>{playing ? '⏸' : '▶'}</button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div onClick={seek} style={{ height: 6, background: '#e5e7eb', borderRadius: 999, overflow: 'hidden', cursor: 'pointer' }}>
          <div style={{ height: '100%', width: `${pct}%`, background: ORANGE, borderRadius: 999 }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#6b7280', marginTop: 5 }}>
          <span>{fmtTime(cur)}</span><span>{fmtTime(dur)}</span>
        </div>
      </div>
    </div>
  );
}

export default function Podcasts() {
  const navigate = useNavigate();
  const [channels, setChannels] = useState(null);
  const [picked, setPicked] = useState(null);   // the chosen channel
  const [brief, setBrief] = useState(null);     // its latest episode
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    apiFetch('/morning-briefs/channels')
      .then(d => setChannels(Array.isArray(d) ? d : []))
      .catch(e => setErr(e?.error || 'Could not load the podcast list.'));
  }, []);

  function open(ch) {
    setPicked(ch); setBrief(null); setLoading(true); setErr('');
    const qs = new URLSearchParams({ audience: ch.audience, limit: '1' });
    if (ch.pm_id) qs.set('pm_id', ch.pm_id);
    if (ch.audience_key) qs.set('audience_key', ch.audience_key);
    apiFetch(`/morning-briefs?${qs.toString()}`)
      .then(d => setBrief(Array.isArray(d) && d.length ? d[0] : null))
      .catch(e => setErr(e?.error || 'Could not load that podcast.'))
      .finally(() => setLoading(false));
  }

  // ── Playing a channel ──────────────────────────────────────────────────────
  if (picked) {
    const paras = String(brief?.summary || '').split(/\n{2,}/).map(s => s.trim()).filter(Boolean);
    return (
      <div className="page" style={{ paddingBottom: 30 }}>
        <div className="top-bar">
          <button className="back-btn-inline" onClick={() => { setPicked(null); setBrief(null); }} aria-label="Back to the podcast list">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M11 4L6 9l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="top-bar-title">{groupIcon(picked.audience)} {picked.label}</div>
            <div className="top-bar-sub">{brief ? fmtDay(brief.brief_date) : 'Daily brief'}</div>
          </div>
        </div>
        <div style={{ padding: '14px 16px' }}>
          {loading && <div style={{ color: '#9ca3af', fontSize: 14 }}>Loading…</div>}
          {err && <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 14 }}>{err}</div>}
          {!loading && !err && !brief && (
            <div style={{ textAlign: 'center', color: '#9ca3af', padding: '40px 10px' }}>
              <div style={{ fontSize: 30, marginBottom: 8 }}>🎙️</div>
              No episode posted for this one yet.
            </div>
          )}
          {brief && (
            <>
              <Player brief={brief} />
              {paras.length > 0 && (
                <>
                  <div style={{ fontSize: 11, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, margin: '18px 0 8px' }}>The rundown</div>
                  <div style={{ fontSize: 14.5, lineHeight: 1.65, color: '#374151' }}>
                    {paras.map((p, i) => <p key={i} style={{ margin: '0 0 11px' }}>{p}</p>)}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // ── Channel picker ─────────────────────────────────────────────────────────
  const groups = {};
  (channels || []).forEach(c => { (groups[c.audience] = groups[c.audience] || []).push(c); });
  const order = Object.keys(groups).sort((a, b) => (GROUP_ORDER[a] ?? 9) - (GROUP_ORDER[b] ?? 9));

  return (
    <div className="page" style={{ paddingBottom: 30 }}>
      <div className="top-bar">
        <button className="back-btn-inline" onClick={() => navigate('/dashboard')} aria-label="Back">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M11 4L6 9l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="top-bar-title">🎙️ Daily Podcast</div>
          <div className="top-bar-sub">Pick which brief to listen to</div>
        </div>
      </div>

      <div style={{ padding: '14px 16px' }}>
        {err && <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 14 }}>{err}</div>}
        {!channels && !err && <div style={{ color: '#9ca3af', fontSize: 14 }}>Loading podcasts…</div>}
        {channels && channels.length === 0 && (
          <div style={{ textAlign: 'center', color: '#9ca3af', padding: '44px 10px' }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>🎙️</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#111', marginBottom: 4 }}>No podcasts yet.</div>
            <div style={{ fontSize: 13.5, lineHeight: 1.5 }}>The daily briefs post each morning around 5:30 AM.</div>
          </div>
        )}
        {order.map(a => (
          <div key={a} style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>{GROUP_TITLE[a] || a}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {groups[a].sort((x, y) => x.label.localeCompare(y.label)).map(ch => (
                <button key={`${ch.audience}:${ch.pm_id || ch.audience_key || ''}`} onClick={() => open(ch)}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', background: '#fff', border: '1.5px solid #ececec', borderRadius: 14, padding: '13px 14px', cursor: 'pointer' }}>
                  <span style={{ flexShrink: 0, width: 42, height: 42, borderRadius: 12, background: '#eef2f7', color: NAVY, fontSize: 19, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{groupIcon(ch.audience)}</span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 15, fontWeight: 800, color: '#111' }}>{ch.label}</span>
                    <span style={{ display: 'block', fontSize: 12, color: '#8a8a8a' }}>{fmtDay(ch.latest)} · {ch.episodes} episode{ch.episodes === 1 ? '' : 's'}</span>
                  </span>
                  <span style={{ flexShrink: 0, color: ORANGE, fontSize: 20 }}>▶</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
